"use client";

import { useEffect, useState, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  Paper,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Avatar,
  Divider,
} from "@mui/material";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import axios from "axios";
import { countries } from "../data/data";
import { Snackbar, Alert } from "@mui/material";
import { arrowBack } from "ionicons/icons";
import { QRCodeCanvas } from "qrcode.react";
import {
  ArrowBack,
  ContentCopy,
  QrCode,
  Visibility,
  Add,
  Download,
  Close,
  Share,
  Work,
  Launch,
  Edit,
  Delete, // Added Delete icon
} from "@mui/icons-material";
import { baseUrl, requestID, config } from "../../../common/utils";

const ProjectDetail = () => {
  const uselocation = useLocation();
  const queryParams = new URLSearchParams(uselocation.search);
  const id = queryParams.get("id");
  console.log(id);
  const data = uselocation.state?.["data"];
  const history = useHistory();

  // State declarations - Replaced budget with minsalary and maxsalary
  const [formFields, setFormFields] = useState({
    jobTitle: "",
    minsalary: "", // New field
    maxsalary: "", // New field
    willingToRelocate: "",
    remoteWorkPreference: "",
    employmentType: "",
    preferredIndustries: "",
    availability: "",
    jobFunction: "",
    nationality: "",
    state: "",
    city: "",
    numberOfPeople: "",
    description: "",
    responsibilities: "",
    highestEducationLevel: "",
    place: "",
  });

  const [selectedRole, setSelectedRole] = useState<any | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false); // Added delete modal state
  const [editingRole, setEditingRole] = useState<any | null>(null);
  const [deletingRole, setDeletingRole] = useState<any | null>(null); // Added deleting role state
  const [customRoles, setCustomRoles] = useState<any[]>([]);
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: "",
  });
  const [jobRolesData, setJobRolesData] = useState<any[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [qrCode, setQRCode] = useState(false);
  const [qrModal, setQRModal] = useState(false);
  const [value, setValue] = useState("");

  const handleFieldChange = (field: string, value: string) => {
    setFormFields((prev) => ({ ...prev, [field]: value }));
  };

  // Fix handleView function
  const handleView = (role: any) => {
    console.log("View role:", role);
    setSelectedRole(role);
    setOpenViewModal(true);
  };

  // Enhanced handleEdit function with all fields including new ones
  const handleEdit = (role: any) => {
    console.log("Edit role:", role);
    setEditingRole(role);
    
    // Pre-fill form fields with existing role data
    setFormFields(prev => ({
      ...prev,
      jobTitle: role.job_title || "",
      minsalary: role.minsalary || "", // New field
      maxsalary: role.maxsalary || "", // New field
      willingToRelocate: role.willing_to_relocate || "",
      remoteWorkPreference: role.remote_work_preference || "",
      employmentType: role.employment_type || "",
      preferredIndustries: role.preferred_industries || "",
      availability: role.availability || "",
      jobFunction: role.job_function || "",
      nationality: role.nationality || "",
      state: role.state || "",
      city: role.city || "",
      numberOfPeople: role.number_required || "",
      description: role.description || "",
      responsibilities: role.responsibilities || "",
      highestEducationLevel: role.highest_education_level || "",
      place: role.place || "",
    }));
    setOpenEditModal(true);
  };

  // Handle delete confirmation
  const handleDelete = (role: any) => {
    console.log("Delete role:", role);
    setDeletingRole(role);
    setOpenDeleteModal(true);
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (!deletingRole) return;

    setStatus({ loading: true, error: false, message: "Deleting job role..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("jobrole_id", deletingRole.jobrole_id);
    formData.append("project_id", data.project_id);

    try {
      const response = await axios.post(
        baseUrl() + "projects/deleteJobRole",
        formData,
        config
      );

      console.log(response);
      setStatus({ loading: false, error: false, message: "" });
      setSnackbarMessage("Job role deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenDeleteModal(false);
      setDeletingRole(null);
      
      // Refresh the job roles list
      getAllRole();
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to delete job role",
      });
      setSnackbarMessage("Failed to delete job role");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setOpenDeleteModal(false);
    setDeletingRole(null);
  };

  // Update job role function - Replaced budget with minsalary and maxsalary
  async function updateJobRole() {
    if (!editingRole) return;

    setStatus({ loading: true, error: false, message: "Updating job role..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("jobrole_id", editingRole.jobrole_id);
    formData.append("project_id", data.project_id);
    formData.append("job_title", formFields.jobTitle);
    formData.append("minsalary", formFields.minsalary); // New field
    formData.append("maxsalary", formFields.maxsalary); // New field
    formData.append("willing_to_relocate", formFields.willingToRelocate);
    formData.append("remote_work_preference", formFields.remoteWorkPreference);
    formData.append("employment_type", formFields.employmentType);
    formData.append("preferred_industries", formFields.preferredIndustries);
    formData.append("availability", formFields.availability);
    formData.append("job_function", formFields.jobFunction);
    formData.append("nationality", formFields.nationality);
    formData.append("state", formFields.state);
    formData.append("city", formFields.city);
    formData.append("number_required", formFields.numberOfPeople);
    formData.append("description", formFields.description);
    formData.append("responsibilities", formFields.responsibilities);
    formData.append("highest_education_level", formFields.highestEducationLevel);
    formData.append("place", formFields.place);

    try {
      const response = await axios.post(
        baseUrl() + "projects/updateJobRole",
        formData,
        config
      );

      console.log(response);
      setStatus({ loading: false, error: false, message: "" });
      setSnackbarMessage("Job role updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenEditModal(false);
      setEditingRole(null);
      
      // Reset form fields
      setFormFields({
        jobTitle: "",
        minsalary: "", // Reset new field
        maxsalary: "", // Reset new field
        willingToRelocate: "",
        remoteWorkPreference: "",
        employmentType: "",
        preferredIndustries: "",
        availability: "",
        jobFunction: "",
        nationality: "",
        state: "",
        city: "",
        numberOfPeople: "",
        description: "",
        responsibilities: "",
        highestEducationLevel: "",
        place: "",
      });
      
      getAllRole();
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to update job role",
      });
      setSnackbarMessage("Failed to update job role");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }

  const handleAddNewRole = () => {
    if (
      !formFields.jobTitle ||
      !formFields.minsalary ||
      !formFields.maxsalary ||
      !formFields.numberOfPeople
    )
      return;
    const newRole = {
      jobTitle: formFields.jobTitle,
      minsalary: formFields.minsalary,
      maxsalary: formFields.maxsalary,
      numberOfPeople: formFields.numberOfPeople,
    };
    setCustomRoles((prev) => [...prev, newRole]);
    setFormFields((prev) => ({
      ...prev,
      jobTitle: "",
      minsalary: "",
      maxsalary: "",
      numberOfPeople: "",
    }));
    setOpenAddModal(false);
  };

  const handleQRCode = (role: any) => {
    setQRCode(role);
    setQRModal(true);
    setValue(
      `${baseUrl()}/projects/view/job_id=${role.jobrole_id}&project_id=${
        role.project_id
      }`
    );
  };

  // Create job role function - Replaced budget with minsalary and maxsalary
  async function createJobRole() {
    setStatus({ loading: true, error: false, message: "Creating project..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("jobrole_id", "0");
    formData.append("project_id", data.project_id);
    formData.append("job_title", formFields.jobTitle);
    formData.append("minsalary", formFields.minsalary); // New field
    formData.append("maxsalary", formFields.maxsalary); // New field
    formData.append("willing_to_relocate", formFields.willingToRelocate);
    formData.append("remote_work_preference", formFields.remoteWorkPreference);
    formData.append("employment_type", formFields.employmentType);
    formData.append("preferred_industries", formFields.preferredIndustries);
    formData.append("availability", formFields.availability);
    formData.append("job_function", formFields.jobFunction);
    formData.append("nationality", formFields.nationality);
    formData.append("state", formFields.state);
    formData.append("city", formFields.city);
    formData.append("number_required", formFields.numberOfPeople);
    formData.append("description", formFields.description);
    formData.append("responsibilities", formFields.responsibilities);
    formData.append("highest_education_level", formFields.highestEducationLevel);
    formData.append("place", formFields.place);

    try {
      const response = await axios.post(
        baseUrl() + "projects/createJobRole",
        formData,
        config
      );

      console.log(response);
      setStatus({ loading: false, error: false, message: "" });
      setSnackbarMessage("Job role created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenAddModal(false);
      getAllRole();

      // Clear all form fields
      setFormFields({
        jobTitle: "",
        minsalary: "", // Reset new field
        maxsalary: "", // Reset new field
        willingToRelocate: "",
        remoteWorkPreference: "",
        employmentType: "",
        preferredIndustries: "",
        availability: "",
        jobFunction: "",
        nationality: "",
        state: "",
        city: "",
        numberOfPeople: "",
        description: "",
        responsibilities: "",
        highestEducationLevel: "",
        place: "",
      });
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to create project",
      });
      setSnackbarMessage("Failed to create job role");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }

  useEffect(() => {
    getAllRole();
  }, []);

  async function getAllRole() {
    setStatus({ loading: true, error: false, message: "Fetching projects..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("project_id", data.project_id);

    try {
      const response = await axios.post(
        baseUrl() + "projects/getJobRolesByProjectId",
        formData,
        config
      );
      setJobRolesData(response.data.data);
      console.log("Job roles data:", response.data.data);
      setStatus({ loading: false, error: false, message: "" });
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to load projects",
      });
    }
  }

  const formatResponsibilities = (text) => {
  if (!text) return "";

  // Split by bullet "•" or by numbered list "1.", "2.", etc.
  const items = text
    .split(/•|\d+\./)
    .map(item => item.trim())
    .filter(item => item.length > 0);

  return items;
};


  const handleCopy = (role) => {
    navigator.clipboard
      .writeText(
        `https://portal.gkgsconsulting.com/applicationPage/?id=${role.jobrole_id}&project_id=${role.project_id}`
      )
      .then(() => {
        setSnackbarMessage(
          `Copied link for ${role.job_title || "the job"} to clipboard!`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      })
      .catch((err) => {
        setSnackbarMessage("Failed to copy text to clipboard");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.error("Failed to copy text: ", err);
      });
  };

  const qrRef = useRef(null);

  const downloadQRCode = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "qr-code.png";
      link.href = url;
      link.click();
    }
  };

  const display = (project_id, jobrole_id) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("project_id", project_id);
      formData.append("jobrole_id", jobrole_id);
      formData.append("response", "view");

      const res = axios.post(
        baseUrl() + "webs/jobrole_view_impression",
        formData,
        config
      );
    } catch (error) {}
  };

  const impression = (project_id, jobrole_id) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("project_id", project_id);
      formData.append("jobrole_id", jobrole_id);
      formData.append("response", "impression");

      const res = axios.post(
        baseUrl() + "webs/jobrole_view_impression",
        formData,
        config
      );
    } catch (error) {}
  };

  const handleShareProject = () => {
    history.push("/folder/assign-project/32");
  };

  // Define the fields that should be shown in View and Edit modals - Removed budget, added minsalary and maxsalary
  const addJobRowFields = [
    "job_title",
    "minsalary", // New field
    "maxsalary", // New field
    "willing_to_relocate",
    "remote_work_preference",
    "employment_type",
    "preferred_industries",
    "availability",
    "job_function",
    "nationality",
    "state",
    "city",
    "number_required",
    "description",
    "responsibilities",
    "highest_education_level",
    "place"
  ];

  // Education level options
  const educationLevels = [
    "High School",
    "Associate Degree",
    "Bachelor's Degree",
    "Master's Degree",
    "Doctorate",
    "Professional Certificate",
    "Diploma",
    "Other"
  ];

  return (
    <IonPage className="pushDown">
      <IonHeader
        style={{
          "--background":
            "linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(168, 85, 247, 1) 100%)",
          "--border-width": "0 0 1px 0",
          "--border-color": "rgba(255, 255, 255, 0.1)",
          "--box-shadow": "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <IonToolbar
          style={{
            "--background":
              "linear-gradient(135deg, rgba(242, 242, 248, 1) 0%, rgba(168, 85, 247, 1) 100%)",
            "--opacity": "1",
            backdropFilter: "blur(0px)",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton style={{ color: "#fff" }} />
          </IonButtons>
        
          <Box mb={4}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              spacing={2}
            >
              <IonButton
                fill="clear"
                onClick={() => history.goBack()}
                style={{
                  border: "1px solid rgba(235, 235, 244, 0.5)",
                  color: "#ededf2ff",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "600",
                  padding: "8px 16px",
                  transition: "all 0.3s ease",
                }}
              >
                <IonIcon icon={arrowBack} style={{ marginRight: "8px" }} />
                Back to Projects
              </IonButton>

              <Button
                variant="contained"
                onClick={handleShareProject}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                  fontWeight: "600",
                  px: 3,
                  py: 1.5,
                  boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(99, 102, 241, 0.4)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Assign Project
              </Button>
            </Stack>
          </Box>
        </IonToolbar>
      </IonHeader>

      <IonContent
        fullscreen
        className="ion-padding"
        style={{
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Box maxWidth="1200px" mx="auto" py={3}>
          {/* Project Summary Card */}
          <Box mb={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "24px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background:
                    "linear-gradient(90deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Avatar
                    sx={{
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                      width: 64,
                      height: 64,
                      mr: 3,
                      boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
                    }}
                  >
                    <Typography variant="h4" color="white" fontWeight="700">
                      {data?.project_name?.charAt(0) || "P"}
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h4"
                      component="h2"
                      sx={{
                        fontWeight: "700",
                        color: "#1e293b",
                        mb: 0.5,
                        fontSize: "28px",
                      }}
                    >
                      Project Name: {data?.project_name}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Project Overview & Details
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.1 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={9}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontWeight: "700",
                        color: "#64748b",
                        letterSpacing: "0.1em",
                        mb: 1,
                        display: "block",
                      }}
                    >
                      PROJECT TAGS
                    </Typography>
                    <Box>
                      {data?.tags?.split(",").map((tag: string, i: number) => (
                        <Chip
                          key={i}
                          label={tag.trim()}
                          sx={{
                            mr: 1,
                            mb: 1,
                            background:
                              "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
                            color: "#6366f1",
                            fontWeight: "600",
                            borderRadius: "12px",
                            border: "1px solid rgba(99, 102, 241, 0.2)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontWeight: "700",
                        color: "#64748b",
                        letterSpacing: "0.1em",
                        mb: 1,
                        display: "block",
                      }}
                    >
                      TOTAL BUDGET
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        background:
                          "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontWeight: "800",
                        fontSize: "24px",
                      }}
                    >
                      {new Intl.NumberFormat("en-NG", {
                        style: "currency",
                        currency: "NGN",
                      }).format(data?.budget_cost) || "₦0"}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Job Roles Section */}
          <Box>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                p={4}
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)",
                  borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "700",
                        color: "#1e293b",
                        mb: 0.5,
                      }}
                    >
                      Job Roles
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Manage positions for{" "}
                      {data?.project_name || "this project"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenAddModal(true)}
                    sx={{
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: "600",
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                      boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
                      px: 4,
                      py: 1.5,
                      fontSize: "16px",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 35px rgba(99, 102, 241, 0.4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Add New Role
                  </Button>
                </Stack>
              </Box>

              {jobRolesData.length > 0 ? (
                <Box sx={{ p: 4 }}>
                  <Stack spacing={3}>
                    {jobRolesData.map((role, index) => (
                      <Card
                        key={index}
                        sx={{
                          borderRadius: "20px",
                          border: "1px solid rgba(226, 232, 240, 0.8)",
                          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          background:
                            "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
                          backdropFilter: "blur(10px)",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                            border: "1px solid rgba(99, 102, 241, 0.3)",
                          },
                          overflow: "hidden",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background:
                              "linear-gradient(90deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 3,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                flex: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: "20px",
                                  background:
                                    "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 3,
                                  flexShrink: 0,
                                  boxShadow:
                                    "0 8px 25px rgba(99, 102, 241, 0.3)",
                                }}
                              >
                                <Work sx={{ color: "white", fontSize: 28 }} />
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: "700",
                                    color: "#1e293b",
                                    mb: 1,
                                    fontSize: "24px",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  Job Title: {role.job_title}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: "#6366f1",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                    mb: 2,
                                  }}
                                >
                                </Typography>
                              </Box>
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "#94a3b8",
                                fontSize: "14px",
                                fontWeight: "500",
                                flexShrink: 0,
                                ml: 2,
                              }}
                            >
                              {role.created_at}
                            </Typography>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                              flexWrap: "wrap",
                              gap: 1,
                              width: "100%",
                              marginTop: "-20px",
                              marginBottom: "20px",
                            }}
                          >
                            <Chip
                              label={`Nationality: ${role.nationality}`}
                              size="medium"
                              sx={{
                                backgroundColor: "#f8fafc",
                                color: "#475569",
                                fontWeight: "600",
                                fontSize: "13px",
                                height: "32px",
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <Chip
                              label={`Employment Type: ${role.employment_type}`}
                              size="medium"
                              sx={{
                                backgroundColor: "#f8fafc",
                                color: "#475569",
                                fontWeight: "600",
                                fontSize: "13px",
                                height: "32px",
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <Chip
                              label={`Education: ${role.highest_education_level || 'Not specified'}`}
                              size="medium"
                              sx={{
                                backgroundColor: "#f8fafc",
                                color: "#475569",
                                fontWeight: "600",
                                fontSize: "13px",
                                height: "32px",
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            {/* Replaced budget with min and max salary chips */}
                            <Chip
                              label={`Min Salary: ₦${new Intl.NumberFormat(
                                "en-NG",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              ).format(role.minsalary || 0)}`}
                              size="medium"
                              sx={{
                                backgroundColor: "#f8fafc",
                                color: "#475569",
                                fontWeight: "600",
                                fontSize: "13px",
                                height: "32px",
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <Chip
                              label={`Max Salary: ₦${new Intl.NumberFormat(
                                "en-NG",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              ).format(role.maxsalary || 0)}`}
                              size="medium"
                              sx={{
                                backgroundColor: "#f8fafc",
                                color: "#475569",
                                fontWeight: "600",
                                fontSize: "13px",
                                height: "32px",
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                              }}
                            />
                          </Stack>

                          {/* Place Field */}
                          {role.place && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#64748b",
                                  fontWeight: "600",
                                  mb: 0.5,
                                  fontSize: "15px",
                                }}
                              >
                                Place:
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#64748b",
                                  fontWeight: "400",
                                  fontSize: "15px",
                                  lineHeight: 1.5,
                                }}
                              >
                                {role.place}
                              </Typography>
                            </Box>
                          )}

                          {/* Description Field */}
                          {role.description && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#64748b",
                                  fontWeight: "600",
                                  mb: 0.5,
                                  fontSize: "15px",
                                }}
                              >
                                Description:
                              </Typography>
                           <Typography
  variant="body1"
  sx={{
    color: "#64748b",
    fontWeight: "400",
    fontSize: "15px",
    lineHeight: 1.5,
    display: "block",
    width: "100%",     // <-- forces full width
    m: 0,              // <-- removes default margins
    p: 0,              // <-- ensures no padding exists
    textAlign: "justify", // optional: stretches words to edges
  }}
>
  {role.description}
</Typography>

                            </Box>
                          )}

                          {/* Responsibilities Field */}
                          {role.responsibilities && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#64748b",
                                  fontWeight: "600",
                                  mb: 0.5,
                                  fontSize: "15px",
                                }}
                              >
                                Responsibilities:
                              </Typography>
                           <Typography
  variant="body1"
  component="ul"
  sx={{
    color: "#64748b",
    fontWeight: "400",
    fontSize: "15px",
    lineHeight: 1.8,
    pl: 2,
    mt: 1,
  }}
>
  {formatResponsibilities(role.responsibilities).map((item, index) => (
    <li key={index} style={{ marginBottom: "8px" }}>
      {item}
    </li>
  ))}
</Typography>

                            </Box>
                          )}

                          <Typography
                            variant="body1"
                            sx={{
                              color: "#64748b",
                              fontWeight: "600",
                              mb: 1,
                              fontSize: "15px",
                            }}
                          >
                            Job Function:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#64748b",
                              fontWeight: "400",
                              mb: 3,
                              fontSize: "15px",
                            }}
                          >
                            {role?.job_function}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "right",
                            }}
                          >
                            <Stack direction="row" spacing={1}>
                              <IconButton
                                onClick={() => handleEdit(role)}
                                sx={{
                                  color: "#64748b",
                                  backgroundColor: "rgba(248, 250, 252, 0.8)",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  width: 44,
                                  height: 44,
                                  "&:hover": {
                                    color: "#6366f1",
                                    backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    border: "1px solid rgba(99, 102, 241, 0.3)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleQRCode(role)}
                                sx={{
                                  color: "#64748b",
                                  backgroundColor: "rgba(248, 250, 252, 0.8)",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  width: 44,
                                  height: 44,
                                  "&:hover": {
                                    color: "#6366f1",
                                    backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    border: "1px solid rgba(99, 102, 241, 0.3)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <QrCode fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleCopy(role)}
                                sx={{
                                  color: "#64748b",
                                  backgroundColor: "rgba(248, 250, 252, 0.8)",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  width: 44,
                                  height: 44,
                                  "&:hover": {
                                    color: "#6366f1",
                                    backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    border: "1px solid rgba(99, 102, 241, 0.3)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleView(role)}
                                sx={{
                                  color: "#64748b",
                                  backgroundColor: "rgba(248, 250, 252, 0.8)",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  width: 44,
                                  height: 44,
                                  "&:hover": {
                                    color: "#6366f1",
                                    backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    border: "1px solid rgba(99, 102, 241, 0.3)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              {/* Delete Button */}
                              <IconButton
                                onClick={() => handleDelete(role)}
                                sx={{
                                  color: "#64748b",
                                  backgroundColor: "rgba(248, 250, 252, 0.8)",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  width: 44,
                                  height: 44,
                                  "&:hover": {
                                    color: "#ef4444",
                                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                                    border: "1px solid rgba(239, 68, 68, 0.3)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box p={6} textAlign="center">
                  <Box
                    sx={{
                      opacity: 0.6,
                      mb: 3,
                      background:
                        "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
                      borderRadius: "50%",
                      width: 120,
                      height: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                    }}
                  >
                    <Add sx={{ fontSize: 48, color: "#64748b" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#475569",
                      fontWeight: "600",
                      mb: 1,
                    }}
                  >
                    No job roles found
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#64748b",
                      mb: 3,
                    }}
                    >
                    Create your first job role to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenAddModal(true)}
                    sx={{
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: "600",
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                      boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
                      px: 4,
                      py: 1.5,
                      fontSize: "16px",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 35px rgba(99, 102, 241, 0.4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Add Your First Role
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Add New Role Modal */}
        <Dialog
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          fullWidth
          maxWidth="lg"
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: "24px",
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
              color: "white",
              fontWeight: "700",
              py: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center">
              <Add sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Create New Job Role
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Define requirements and specifications
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenAddModal(false)}
              sx={{
                color: "white",
                "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 4, px: 3 }}>
            <Grid container spacing={4} sx={{ py: 2 }}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Basic Information
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Essential details about the job role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Job Title"
                  fullWidth
                  value={formFields.jobTitle}
                  onChange={(e) =>
                    handleFieldChange("jobTitle", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Replaced budget with minsalary and maxsalary */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Salary"
                  fullWidth
                  type="number"
                  value={formFields.minsalary}
                  onChange={(e) => handleFieldChange("minsalary", e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <span
                        style={{
                          marginRight: 8,
                          color: "#6366f1",
                          fontWeight: "600",
                        }}
                      >
                        ₦
                      </span>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Maximum Salary"
                  fullWidth
                  type="number"
                  value={formFields.maxsalary}
                  onChange={(e) => handleFieldChange("maxsalary", e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <span
                        style={{
                          marginRight: 8,
                          color: "#6366f1",
                          fontWeight: "600",
                        }}
                      >
                        ₦
                      </span>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* New Fields: Highest Education Level and Place */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Highest Education Level</InputLabel>
                  <Select
                    value={formFields.highestEducationLevel}
                    onChange={(e) =>
                      handleFieldChange("highestEducationLevel", e.target.value)
                    }
                    label="Highest Education Level"
                  >
                    {educationLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Place"
                  fullWidth
                  value={formFields.place}
                  onChange={(e) => handleFieldChange("place", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Remote Work Preference and Willing to Relocate */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Willing to Relocate</InputLabel>
                  <Select
                    value={formFields.willingToRelocate}
                    onChange={(e) =>
                      handleFieldChange("willingToRelocate", e.target.value)
                    }
                    label="Willing to Relocate"
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Remote Work Preference</InputLabel>
                  <Select
                    value={formFields.remoteWorkPreference}
                    onChange={(e) =>
                      handleFieldChange("remoteWorkPreference", e.target.value)
                    }
                    label="Remote Work Preference"
                  >
                    <MenuItem value="Fully_Remote">Fully_Remote</MenuItem>
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                    <MenuItem value="On-site">On-site</MenuItem>
                    <MenuItem value="Flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Nationality</InputLabel>
                  <Select
                    value={formFields.nationality}
                    onChange={(e) =>
                      handleFieldChange("nationality", e.target.value)
                    }
                    label="Nationality"
                  >
                    {countries.map((nation) => (
                      <MenuItem key={nation.code} value={nation.name}>
                        {nation.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Number of People Required"
                  fullWidth
                  type="number"
                  value={formFields.numberOfPeople}
                  onChange={(e) =>
                    handleFieldChange("numberOfPeople", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    value={formFields.employmentType}
                    onChange={(e) =>
                      handleFieldChange("employmentType", e.target.value)
                    }
                    label="Employment Type"
                  >
                    <MenuItem value="full-time">Full-time</MenuItem>
                    <MenuItem value="part-time">Part-time</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="temporary">Temporary</MenuItem>
                    <MenuItem value="internship">Internship</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Availability</InputLabel>
                  <Select
                    value={formFields.availability}
                    onChange={(e) =>
                      handleFieldChange("availability", e.target.value)
                    }
                    label="Availability"
                  >
                    <MenuItem value="ImmediatelyAvailable">
                      Immediately Available
                    </MenuItem>
                    <MenuItem value="1week">1 week notice</MenuItem>
                    <MenuItem value="2weeks">2 weeks notice</MenuItem>
                    <MenuItem value="1month">1 month notice</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Job Function"
                  fullWidth
                  size="small"
                  value={formFields.jobFunction}
                  onChange={(e) =>
                    handleFieldChange("jobFunction", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Preferred Industries"
                  fullWidth
                  size="small"
                  value={formFields.preferredIndustries}
                  onChange={(e) =>
                    handleFieldChange("preferredIndustries", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  fullWidth
                  size="small"
                  value={formFields.city}
                  onChange={(e) => handleFieldChange("city", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="State"
                  fullWidth
                  size="small"
                  value={formFields.state}
                  onChange={(e) => handleFieldChange("state", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Description Field */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Job Description
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Detailed description of the job role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={formFields.description}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  variant="outlined"
                  placeholder="Enter detailed job description..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Responsibilities Field */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Responsibilities
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Key responsibilities and duties for this role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Responsibilities"
                  fullWidth
                  multiline
                  rows={4}
                  value={formFields.responsibilities}
                  onChange={(e) => handleFieldChange("responsibilities", e.target.value)}
                  variant="outlined"
                  placeholder="Enter key responsibilities and duties..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button
              onClick={() => setOpenAddModal(false)}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                color: "#64748b",
                fontWeight: "600",
                px: 3,
                py: 1.5,
                "&:hover": {
                  background: "rgba(100, 116, 139, 0.1)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={createJobRole}
              variant="contained"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "600",
                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                px: 4,
                py: 1.5,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {status.loading ? "Creating..." : "Create Role"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Role Details Modal - Shows all fields including new ones */}
        <Dialog
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: "24px",
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
              color: "white",
              fontWeight: "700",
              py: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center">
              <Visibility sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Job Role Details
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Complete job requirements and specifications
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenViewModal(false)}
              sx={{
                color: "white",
                "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 4, px: 3 }}>
            {selectedRole && (
              <Grid container spacing={3}>
                {Object.entries(selectedRole)
                  .filter(([key]) => 
                    addJobRowFields.includes(key) && 
                    !["project_id", "jobrole_id", "id"].includes(key)
                  )
                  .map(([key, value]) => {
                    const displayValue =
                      key.toLowerCase().includes("salary") ||
                      key.toLowerCase().includes("cost")
                        ? `₦${parseFloat(value || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : value || "Not specified";

                    return (
                      <Grid item xs={12} sm={6} lg={4} key={key}>
                        <Box
                          sx={{
                            p: 3,
                            background:
                              "linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)",
                            borderRadius: "16px",
                            height: "100%",
                            border: "1px solid rgba(226, 232, 240, 0.5)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{
                              color: "#64748b",
                              fontWeight: "700",
                              letterSpacing: "0.1em",
                              mb: 1,
                              display: "block",
                            }}
                          >
                            {key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").toUpperCase()}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: "600",
                              color: value ? "#1e293b" : "#94a3b8",
                              fontStyle: value ? "normal" : "italic",
                            }}
                          >
                            {displayValue}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button
              onClick={() => setOpenViewModal(false)}
              variant="contained"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "600",
                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                px: 4,
                py: 1.5,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Close Details
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Job Role Modal - Shows all fields including new ones */}
        <Dialog
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          fullWidth
          maxWidth="lg"
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: "24px",
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
              color: "white",
              fontWeight: "700",
              py: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center">
              <Edit sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Edit Job Role
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Update job requirements and specifications
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenEditModal(false)}
              sx={{
                color: "white",
                "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 4, px: 3 }}>
            <Grid container spacing={4} sx={{ py: 2 }}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Basic Information
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Essential details about the job role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Job Title"
                  fullWidth
                  value={formFields.jobTitle}
                  onChange={(e) =>
                    handleFieldChange("jobTitle", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Replaced budget with minsalary and maxsalary */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Salary"
                  fullWidth
                  type="number"
                  value={formFields.minsalary}
                  onChange={(e) => handleFieldChange("minsalary", e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <span
                        style={{
                          marginRight: 8,
                          color: "#6366f1",
                          fontWeight: "600",
                        }}
                      >
                        ₦
                      </span>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Maximum Salary"
                  fullWidth
                  type="number"
                  value={formFields.maxsalary}
                  onChange={(e) => handleFieldChange("maxsalary", e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <span
                        style={{
                          marginRight: 8,
                          color: "#6366f1",
                          fontWeight: "600",
                        }}
                      >
                        ₦
                      </span>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* New Fields: Highest Education Level and Place */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Highest Education Level</InputLabel>
                  <Select
                    value={formFields.highestEducationLevel}
                    onChange={(e) =>
                      handleFieldChange("highestEducationLevel", e.target.value)
                    }
                    label="Highest Education Level"
                  >
                    {educationLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Place"
                  fullWidth
                  value={formFields.place}
                  onChange={(e) => handleFieldChange("place", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Remote Work Preference and Willing to Relocate */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Willing to Relocate</InputLabel>
                  <Select
                    value={formFields.willingToRelocate}
                    onChange={(e) =>
                      handleFieldChange("willingToRelocate", e.target.value)
                    }
                    label="Willing to Relocate"
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Remote Work Preference</InputLabel>
                  <Select
                    value={formFields.remoteWorkPreference}
                    onChange={(e) =>
                      handleFieldChange("remoteWorkPreference", e.target.value)
                    }
                    label="Remote Work Preference"
                  >
                    <MenuItem value="Fully_Remote">Fully_Remote</MenuItem>
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                    <MenuItem value="On-site">On-site</MenuItem>
                    <MenuItem value="Flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Nationality</InputLabel>
                  <Select
                    value={formFields.nationality}
                    onChange={(e) =>
                      handleFieldChange("nationality", e.target.value)
                    }
                    label="Nationality"
                  >
                    {countries.map((nation) => (
                      <MenuItem key={nation.code} value={nation.name}>
                        {nation.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Number of People Required"
                  fullWidth
                  type="number"
                  value={formFields.numberOfPeople}
                  onChange={(e) =>
                    handleFieldChange("numberOfPeople", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    value={formFields.employmentType}
                    onChange={(e) =>
                      handleFieldChange("employmentType", e.target.value)
                    }
                    label="Employment Type"
                  >
                    <MenuItem value="full-time">Full-time</MenuItem>
                    <MenuItem value="part-time">Part-time</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="temporary">Temporary</MenuItem>
                    <MenuItem value="internship">Internship</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                >
                  <InputLabel>Availability</InputLabel>
                  <Select
                    value={formFields.availability}
                    onChange={(e) =>
                      handleFieldChange("availability", e.target.value)
                    }
                    label="Availability"
                  >
                    <MenuItem value="ImmediatelyAvailable">
                      Immediately Available
                    </MenuItem>
                    <MenuItem value="1week">1 week notice</MenuItem>
                    <MenuItem value="2weeks">2 weeks notice</MenuItem>
                    <MenuItem value="1month">1 month notice</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Job Function"
                  fullWidth
                  size="small"
                  value={formFields.jobFunction}
                  onChange={(e) =>
                    handleFieldChange("jobFunction", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Preferred Industries"
                  fullWidth
                  size="small"
                  value={formFields.preferredIndustries}
                  onChange={(e) =>
                    handleFieldChange("preferredIndustries", e.target.value)
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  fullWidth
                  size="small"
                  value={formFields.city}
                  onChange={(e) => handleFieldChange("city", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="State"
                  fullWidth
                  size="small"
                  value={formFields.state}
                  onChange={(e) => handleFieldChange("state", e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Description Field */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Job Description
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Detailed description of the job role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={formFields.description}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  variant="outlined"
                  placeholder="Enter detailed job description..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>

              {/* Responsibilities Field */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#6366f1",
                      fontWeight: "700",
                      mb: 0.5,
                    }}
                  >
                    Responsibilities
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    Key responsibilities and duties for this role
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Responsibilities"
                  fullWidth
                  multiline
                  rows={4}
                  value={formFields.responsibilities}
                  onChange={(e) => handleFieldChange("responsibilities", e.target.value)}
                  variant="outlined"
                  placeholder="Enter key responsibilities and duties..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.8)",
                      "&:hover fieldset": {
                        borderColor: "#6366f1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#6366f1",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#6366f1",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button
              onClick={() => setOpenEditModal(false)}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                color: "#64748b",
                fontWeight: "600",
                px: 3,
                py: 1.5,
                "&:hover": {
                  background: "rgba(100, 116, 139, 0.1)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={updateJobRole}
              variant="contained"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "600",
                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                px: 4,
                py: 1.5,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {status.loading ? "Updating..." : "Update Role"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={openDeleteModal}
          onClose={cancelDelete}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "24px",
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "white",
              fontWeight: "700",
              py: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center">
              <Delete sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Delete Job Role
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Confirm deletion of this job role
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={cancelDelete}
              sx={{
                color: "white",
                "&:hover": { background: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 4, px: 3 }}>
            <Box textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <Delete sx={{ fontSize: 40, color: "#ef4444" }} />
              </Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b" mb={2}>
                Are you sure you want to delete this job role?
              </Typography>
              {deletingRole && (
                <Typography variant="body1" color="#64748b" mb={3}>
                  You are about to delete the job role:{" "}
                  <strong>"{deletingRole.job_title}"</strong>. This action cannot be
                  undone and all associated data will be permanently removed.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button
              onClick={cancelDelete}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                color: "#64748b",
                fontWeight: "600",
                px: 4,
                py: 1.5,
                "&:hover": {
                  background: "rgba(100, 116, 139, 0.1)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "600",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                px: 4,
                py: 1.5,
                "&:hover": {
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {status.loading ? "Deleting..." : "Yes, Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add QR Code Modal and other modals as needed */}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </IonContent>
    </IonPage>
  );
};

export default ProjectDetail;